const WUID_KEY = 'etap_landing_wuid';
const LEGACY_SESSION_KEY = 'etap_landing_wuid';
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.etap-app.ru';

/**
 * WUID хранится в localStorage, а не в sessionStorage: вход через Yandex ID
 * уводит на сторонний домен, Telegram-виджет может открыться в новой вкладке,
 * а Mini App каждый раз стартует в свежем webview — sessionStorage такого не
 * переживает, и источник визита терялся до отправки на сервер.
 */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Приватный режим — атрибутировать нечем, но вход ломать нельзя.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
}

/** Читает ?wuid= из URL и сохраняет (вызывать при старте, до авторизации). */
export function storeLandingWuid(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const wuid = params.get('wuid');
  if (wuid) {
    safeSet(WUID_KEY, wuid);
    return;
  }

  // Хвост от прежней версии: переносим из sessionStorage, чтобы не потерять
  // источник у тех, кто уже в процессе входа.
  try {
    const legacy = window.sessionStorage.getItem(LEGACY_SESSION_KEY);
    if (legacy && !safeGet(WUID_KEY)) {
      safeSet(WUID_KEY, legacy);
      window.sessionStorage.removeItem(LEGACY_SESSION_KEY);
    }
  } catch {
    // no-op
  }
}

/**
 * Возвращает WUID, НЕ удаляя его.
 *
 * Раньше значение стиралось при чтении — и если авторизация после этого падала
 * (невалидный initData, отказ в OAuth, сетевая ошибка), источник визита
 * пропадал навсегда, хотя пользователь оставался тем же. Теперь удаление
 * происходит только после успешной регистрации, через clearLandingWuid().
 */
export function consumeLandingWuid(): string | null {
  return safeGet(WUID_KEY);
}

/** Удаляет WUID — вызывать после того, как регистрация реально состоялась. */
export function clearLandingWuid(): void {
  safeRemove(WUID_KEY);
}

/** Отправляет событие user_registered в tracking API. */
export async function sendRegistrationEvent(wuid: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/tracking/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wuid,
        eventName: 'user_registered',
        eventType: 'conversion',
        source: 'webapp',
        path: window.location.pathname,
        occurredAt: new Date().toISOString(),
      }),
      mode: 'cors',
      keepalive: true,
    });
  } catch {
    // silent fail
  }
}

/**
 * Замыкает воронку: регистрация состоялась — фиксируем конверсию и убираем
 * WUID, чтобы следующий вход не посчитался повторно.
 *
 * Функция sendRegistrationEvent существовала и раньше, но её никто не вызывал:
 * событие user_registered в guest_events не появлялось ни разу, и связать
 * визит с регистрацией было нечем даже там, где WUID доезжал.
 */
export function reportRegistration(isNewUser: boolean | undefined): void {
  const wuid = consumeLandingWuid();
  if (!wuid) return;

  if (isNewUser) {
    void sendRegistrationEvent(wuid);
  }

  clearLandingWuid();
}
